import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  CheckCircle2, XCircle, Clock, AlertCircle, User, Building, 
  RefreshCw, MessageSquare, ChevronRight, FileText
} from 'lucide-react';
import { toast } from 'sonner';

export default function PendingApprovals() {
  const { user } = useAuth();
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [pendingActions, setPendingActions] = useState([]);
  const [processedActions, setProcessedActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedAction, setSelectedAction] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);
  const [employeeHistory, setEmployeeHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const isDirector = user?.role === 'director' || user?.role === 'super_admin';
  const isHR = user?.role === 'hr';
  const canApprove = isDirector || isHR;

  useEffect(() => {
    fetchActions();
  }, []);

  const fetchActions = async () => {
    try {
      const [pending, approved, rejected] = await Promise.all([
        api.get('/pending-actions', { params: { status: 'pending' } }),
        api.get('/pending-actions', { params: { status: 'approved' } }),
        api.get('/pending-actions', { params: { status: 'rejected' } })
      ]);
      setPendingActions(pending.data);
      setProcessedActions([...approved.data, ...rejected.data].sort((a, b) => 
        new Date(b.reviewed_at || b.requested_at) - new Date(a.reviewed_at || a.requested_at)
      ));
    } catch (error) {
      toast.error('Erreur lors du chargement des actions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedAction) return;
    setProcessing(true);
    try {
      await api.post(`/pending-actions/${selectedAction.id}/approve`, null, {
        params: { comment: comment || null }
      });
      toast.success('Action approuvée avec succès');
      setReviewModalOpen(false);
      setSelectedAction(null);
      setComment('');
      fetchActions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'approbation');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAction) return;
    if (!comment.trim()) {
      toast.error('Veuillez indiquer un motif de refus');
      return;
    }
    setProcessing(true);
    try {
      await api.post(`/pending-actions/${selectedAction.id}/reject`, null, {
        params: { comment }
      });
      toast.success('Action refusée');
      setReviewModalOpen(false);
      setSelectedAction(null);
      setComment('');
      fetchActions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setProcessing(false);
    }
  };

  const openReviewModal = async (action) => {
    setSelectedAction(action);
    setComment('');
    setEmployeeHistory(null);
    setReviewModalOpen(true);
    
    // Fetch employee's schedule exception history if it's a schedule_exception action
    if (action.action_type === 'schedule_exception' && action.target_id) {
      setLoadingHistory(true);
      try {
        const response = await api.get(`/employees/${action.target_id}/schedule-exceptions-history`);
        setEmployeeHistory(response.data);
      } catch (error) {
        console.error('Failed to fetch employee history:', error);
      } finally {
        setLoadingHistory(false);
      }
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'employee_status_change':
        return <User className="w-5 h-5 text-amber-500" />;
      case 'department_change':
        return <Building className="w-5 h-5 text-blue-500" />;
      case 'leave_request':
        return <FileText className="w-5 h-5 text-green-500" />;
      case 'schedule_exception':
        return <Clock className="w-5 h-5 text-purple-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-slate-500" />;
    }
  };

  const getActionTypeLabel = (type) => {
    const labels = {
      'employee_status_change': 'Changement de statut',
      'department_change': 'Changement de direction',
      'leave_request': 'Demande de congé',
      'employee_create': 'Création d\'employé',
      'schedule_exception': 'Exception d\'horaire'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" />Approuvé</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Refusé</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  // Filtre par entreprise sélectionnée
  const scopedPending = isCrossCompany && selectedCompanyId
    ? pendingActions.filter(a => a.company_id === selectedCompanyId)
    : pendingActions;

  const scopedProcessed = isCrossCompany && selectedCompanyId
    ? processedActions.filter(a => a.company_id === selectedCompanyId)
    : processedActions;

  // Filter pending actions based on user role
  // HR can only see employee-initiated schedule exceptions
  // Directors can see all
  const filteredPendingActions = scopedPending.filter(action => {
    if (isDirector) return true;
    if (isHR) {
      // HR can only see employee-initiated schedule exceptions
      return action.action_type === 'schedule_exception' && action.is_employee_request;
    }
    // Non-approvers see only their own requests
    return action.requested_by === user?.id;
  });

  return (
    <div className="space-y-6" data-testid="pending-approvals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">
            {canApprove ? 'Validations en attente' : 'Mes demandes'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isDirector 
              ? 'Actions soumises par les administrateurs, RH et employés nécessitant votre validation'
              : isHR
              ? 'Demandes d\'exceptions d\'horaire des employés à valider'
              : 'Suivez le statut de vos demandes soumises'
            }
          </p>
        </div>
        <Button variant="outline" onClick={fetchActions}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            En attente ({filteredPendingActions.length})
          </TabsTrigger>
          <TabsTrigger value="processed" data-testid="tab-processed">
            Traités ({processedActions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {filteredPendingActions.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12 text-slate-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <p className="text-lg font-medium">Aucune action en attente</p>
                <p className="text-sm">Toutes les demandes ont été traitées</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredPendingActions.map((action) => (
                <Card key={action.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          {getActionIcon(action.action_type)}
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-900">{action.description}</h3>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span>{getActionTypeLabel(action.action_type)}</span>
                            {action.is_employee_request && (
                              <Badge className="bg-purple-100 text-purple-800 text-xs">
                                Demande employé
                              </Badge>
                            )}
                            <span>•</span>
                            <span>Par: {action.requester_name}</span>
                            <span>•</span>
                            <span>{formatDate(action.requested_at)}</span>
                          </div>
                          {action.payload?.reason && (
                            <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">
                              <MessageSquare className="w-3 h-3 inline mr-1" />
                              {action.payload.reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(action.status)}
                        {isDirector && (
                          <Button 
                            size="sm" 
                            onClick={() => openReviewModal(action)}
                            data-testid={`review-action-${action.id}`}
                          >
                            Examiner
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="processed" className="mt-4">
          {processedActions.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12 text-slate-500">
                <p>Aucune action traitée</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {scopedProcessed.map((action) => (
                <Card key={action.id} className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          {getActionIcon(action.action_type)}
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-900">{action.description}</h3>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span>{getActionTypeLabel(action.action_type)}</span>
                            <span>•</span>
                            <span>Par: {action.requester_name}</span>
                            <span>•</span>
                            <span>Traité le: {formatDate(action.reviewed_at)}</span>
                          </div>
                          {action.review_comment && (
                            <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">
                              <MessageSquare className="w-3 h-3 inline mr-1" />
                              Commentaire: {action.review_comment}
                            </p>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(action.status)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Examiner la demande</DialogTitle>
            <DialogDescription>
              Approuvez ou refusez cette action
            </DialogDescription>
          </DialogHeader>
          
          {selectedAction && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">Détails de la demande</h4>
                <p className="text-sm text-slate-600">{selectedAction.description}</p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Type:</span>
                    <span className="ml-2 font-medium">{getActionTypeLabel(selectedAction.action_type)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Demandeur:</span>
                    <span className="ml-2 font-medium">{selectedAction.requester_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Date:</span>
                    <span className="ml-2 font-medium">{formatDate(selectedAction.requested_at)}</span>
                  </div>
                  {selectedAction.is_employee_request && (
                    <div>
                      <Badge className="bg-purple-100 text-purple-800">Demande de l'employé</Badge>
                    </div>
                  )}
                </div>
                {selectedAction.payload?.reason && (
                  <div className="mt-3 p-2 bg-white rounded border">
                    <span className="text-slate-500 text-sm">Motif:</span>
                    <p className="text-sm mt-1">{selectedAction.payload.reason}</p>
                    {selectedAction.payload?.reason_details && (
                      <p className="text-xs text-slate-500 mt-1">{selectedAction.payload.reason_details}</p>
                    )}
                  </div>
                )}
                {/* Schedule exception specific info */}
                {selectedAction.action_type === 'schedule_exception' && selectedAction.payload && (
                  <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-100">
                    <h5 className="font-medium text-blue-900 mb-2">Horaires demandés</h5>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-blue-700">Période:</span>
                        <span className="ml-2 font-medium text-blue-900">
                          {selectedAction.payload.start_date} - {selectedAction.payload.end_date}
                        </span>
                      </div>
                      <div>
                        <span className="text-blue-700">Horaire actuel:</span>
                        <span className="ml-2 font-medium text-blue-900">
                          {selectedAction.payload.original_start_time} - {selectedAction.payload.original_end_time}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-blue-700">Horaire demandé:</span>
                        <span className="ml-2 font-bold text-blue-900">
                          {selectedAction.payload.adjusted_start_time} - {selectedAction.payload.adjusted_end_time}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Employee Schedule Exception History */}
              {selectedAction.action_type === 'schedule_exception' && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                  <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Historique des exceptions de cet employé
                  </h4>
                  {loadingHistory ? (
                    <p className="text-sm text-amber-700">Chargement...</p>
                  ) : employeeHistory ? (
                    <div>
                      <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-lg font-bold text-amber-900">{employeeHistory.total_requests}</p>
                          <p className="text-xs text-amber-700">Total demandes</p>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-lg font-bold text-green-600">{employeeHistory.approved}</p>
                          <p className="text-xs text-green-700">Approuvées</p>
                        </div>
                        <div className="text-center p-2 bg-white rounded">
                          <p className="text-lg font-bold text-red-600">{employeeHistory.rejected}</p>
                          <p className="text-xs text-red-700">Refusées</p>
                        </div>
                      </div>
                      {employeeHistory.history && employeeHistory.history.length > 0 && (
                        <div className="max-h-32 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-amber-100">
                              <tr>
                                <th className="p-1 text-left">Période</th>
                                <th className="p-1 text-left">Horaires</th>
                                <th className="p-1 text-left">Statut</th>
                              </tr>
                            </thead>
                            <tbody>
                              {employeeHistory.history.slice(0, 5).map((h, idx) => (
                                <tr key={idx} className="border-b border-amber-100">
                                  <td className="p-1">{h.start_date} → {h.end_date}</td>
                                  <td className="p-1">{h.adjusted_start_time}-{h.adjusted_end_time}</td>
                                  <td className="p-1">
                                    <span className={`px-1 py-0.5 rounded text-xs ${
                                      h.status === 'approved' || h.status === 'active' || h.status === 'expired' 
                                        ? 'bg-green-100 text-green-800' 
                                        : h.status === 'rejected' 
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-amber-100 text-amber-800'
                                    }`}>
                                      {h.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-amber-700">Aucun historique disponible</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Commentaire (obligatoire pour refus)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2 text-sm"
                  rows={3}
                  placeholder="Ajoutez un commentaire..."
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  onClick={handleReject}
                  disabled={processing}
                  data-testid="reject-button"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Refuser
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleApprove}
                  disabled={processing}
                  data-testid="approve-button"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Approuver
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
